type BifrostEnv = 'prod' | 'staging' | 'dev';
// Environment variables are set by the build process
export const bifrostEnv = process.env.BIFROST_ENV! as BifrostEnv;

/** Amplitude API key for session replay (rovo extension project). */
export const getSessionReplayAmplitudeApiKey = (): string => '4aa1cbd970f59a84661985c72484058f';

export const bitbucketCommit: string | undefined = process.env.BITBUCKET_COMMIT;

export const extensionUrl: string = process.env.EXTENSION_URL!;

// Derived constants based on the environment
export const debugEnabled: boolean = bifrostEnv !== 'prod';

export type StatsigResourceType = 'featureGate' | 'experiment' | 'dynamicConfig';
export type SwitcherooResourceType = 'flag' | 'dynamicConfig';

export const getStatsigUrl = (id: string, type: StatsigResourceType): string => {
	switch (type) {
		case 'featureGate':
			return `https://console.statsig.com/gates/${id}`;
		case 'experiment':
			return `https://console.statsig.com/experiments/${id}/setup`;
		case 'dynamicConfig':
			return `https://console.statsig.com/dynamic_configs/${id}`;
		default:
			throw new Error(`Unknown Statsig resource type: ${type}`);
	}
};

export const getSwitcherooUrl = (id: string, type: SwitcherooResourceType): string => {
	switch (type) {
		case 'flag':
			return `https://switcheroo.atlassian.com/ui/changes?flagSearch=${id}`;
		case 'dynamicConfig':
			return `https://switcheroo.atlassian.com/ui/configurations?search=${id}`;
		default:
			throw new Error(`Unknown Switcheroo resource type: ${type}`);
	}
};

// Helper functions based on the environment
export const getIdentityUrl = (): string => {
	const stagingLoginUrl = 'https://id.stg.internal.atlassian.com';
	const prodLoginUrl = 'https://id.atlassian.com';

	const isProd = bifrostEnv === 'prod';

	if (isProd) {
		return prodLoginUrl;
	}

	return stagingLoginUrl;
};

export const getRovoExtensionWebBaseUrl = (): string => {
	const stagingBaseUrl = 'https://rovo-extension-web.stg-east.frontend.public.atl-paas.net';
	const prodBaseUrl = 'https://rovo-extension-web.atlassian.com';

	const isProd = bifrostEnv === 'prod';

	if (isProd) {
		return prodBaseUrl;
	}

	return stagingBaseUrl;
};

export const getGraphqlUrl = (): string => `${getRovoExtensionWebBaseUrl()}/gateway/api/graphql`;

export const getAtlassianHomeUrl = (): string => {
	const stagingAtlassianHomeUrl = 'https://home.stg.atlassian.com';
	const prodAtlassianHomeUrl = 'https://home.atlassian.com';

	const isProd = bifrostEnv === 'prod';

	if (isProd) {
		return prodAtlassianHomeUrl;
	}

	return stagingAtlassianHomeUrl;
};

export const getAdminHubUrl = (): string => {
	const stagingAdminHubUrl = 'https://admin.stg.atlassian.com';
	const prodAdminHubUrl = 'https://admin.atlassian.com';

	const isProd = bifrostEnv === 'prod';

	if (isProd) {
		return prodAdminHubUrl;
	}

	return stagingAdminHubUrl;
};

export const getStudioUrl = (): string => {
	const stagingStudioUrl = 'https://atlassian-studio.stg-east.frontend.public.atl-paas.net';
	const prodStudioUrl = 'https://studio.atlassian.com';

	const isProd = bifrostEnv === 'prod';

	if (isProd) {
		return prodStudioUrl;
	}

	return stagingStudioUrl;
};

export const getLocale = (): string => {
	return navigator.language;
};

export const getBuildInfo = (): {
	environment: BifrostEnv;
	version?: string;
	buildNumber?: string;
	buildUrl?: string;
} => {
	const environment = bifrostEnv;
	const version = process.env.BITBUCKET_COMMIT;
	const buildNumber = process.env.BITBUCKET_BUILD_NUMBER;
	const buildUrl = buildNumber
		? `https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pipelines/results/${buildNumber}`
		: undefined;

	return {
		environment,
		version,
		buildNumber,
		buildUrl,
	};
};
